import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { collectBomUsageSnapshot } from './bom_usage_snapshot_service.js?v=20260901b';

const STATISTICS_SNAPSHOT_SCHEMA_VERSION = 1;
const STATISTICS_VALUE_TYPES = new Set(['number', 'string', 'boolean', 'date', 'json']);
const DEFAULT_AGGREGATIONS = Object.freeze(['list', 'count']);
const NUMBER_AGGREGATIONS = Object.freeze(['list', 'count', 'sum', 'avg', 'min', 'max']);

const CORE_STATISTICS_ATTRIBUTES = Object.freeze([
  attribute('order.project_number', 'Projektnummer', 'Nummer des Projekts', 'order', 'string'),
  attribute('order.order_number', 'Auftragsnummer', 'Nummer des Auftrags', 'order', 'string'),
  attribute('order.description', 'Auftragsbezeichnung', 'Bezeichnung des Auftrags', 'order', 'string'),
  attribute('order.year', 'Auftragsjahr', 'Jahr, in dem der Auftrag erstellt wurde', 'order', 'number', '', NUMBER_AGGREGATIONS),
  attribute('order.customer_name', 'Kunde', 'Kundenname des Projekts', 'order', 'string'),
  attribute('order.seller', 'Verkäufer', 'Zugeordneter Verkäufer', 'order', 'string'),
  attribute('order.seller_user_id', 'Verkäufer-ID', 'Interne Benutzer-ID des Verkäufers', 'order', 'string'),
  attribute('order.avor', 'AVOR', 'Zugeordnete AVOR-Person', 'order', 'string'),
  attribute('order.avor_user_id', 'AVOR-ID', 'Interne Benutzer-ID der AVOR-Person', 'order', 'string'),
  attribute('company.name', 'Firma', 'Firma des zugeordneten Verkäufers', 'order', 'string'),
  attribute('order.purchase_price_chf', 'Einstandspreis total', 'Summe der historischen Einstandspreise des Auftrags', 'order', 'number', 'CHF', NUMBER_AGGREGATIONS),
  attribute('system.code', 'System', 'Im Auftrag enthaltenes System', 'system', 'string'),
  attribute('system.label', 'Systembezeichnung', 'Bezeichnung des enthaltenen Systems', 'system', 'string'),
  attribute('system.company_code', 'Firma', 'Firma des enthaltenen Systems', 'system', 'string'),
  attribute('system.category_code', 'Systemgruppe', 'Systemgruppe des enthaltenen Systems', 'system', 'string')
]);

const BOM_STATISTICS_ATTRIBUTES = Object.freeze([
  attribute('article.article_no', 'Artikelnummer', 'Artikelnummer der verwendeten BOM-Position', 'article_usage', 'string'),
  attribute('article.label', 'Artikelbezeichnung', 'Bezeichnung der verwendeten BOM-Position', 'article_usage', 'string'),
  attribute('article.source_system', 'Quellsystem', 'System, welches die BOM-Position erzeugt hat', 'article_usage', 'string'),
  attribute('article.quantity', 'Menge', 'Verwendete Artikelmenge', 'article_usage', 'number', '', ['list', 'count', 'sum', 'avg', 'min', 'max']),
  attribute('article.unit', 'Einheit', 'Mengeneinheit der BOM-Position', 'article_usage', 'string'),
  attribute('article.unit_cost_chf', 'Einstandspreis', 'Einstandspreis pro Einheit zum Snapshot-Zeitpunkt', 'article_usage', 'number', 'CHF', ['list', 'sum', 'avg', 'min', 'max']),
  attribute('article.line_cost_chf', 'Einstandspreis total', 'Menge mal Einstandspreis zum Snapshot-Zeitpunkt', 'article_usage', 'number', 'CHF', ['list', 'sum', 'avg', 'min', 'max'])
]);

const CORE_STATISTICS_ADAPTER = Object.freeze({
  id: 'core',
  systemCode: 'core',
  label: 'Auftrag und Projekt',
  schemaVersion: 1,
  attributes: CORE_STATISTICS_ATTRIBUTES,
  collect({ project, presentSystems = [] } = {}) {
    const orderValues = {
      'order.project_number': project?.projectNumber,
      'order.order_number': project?.orderNumber,
      'order.description': project?.orderDescription,
      'order.customer_name': project?.customerName,
      'order.seller': project?.seller,
      'order.seller_user_id': project?.sellerUserId,
      'order.avor': project?.avor,
      'order.avor_user_id': project?.avorUserId
    };
    const systemEntities = presentSystems.map((system, index) => ({
      entityType: 'system',
      sourceId: system.id,
      ordinal: index,
      values: {
        'system.code': system.id,
        'system.label': system.label || system.name || system.id,
        'system.company_code': system.companyId,
        'system.category_code': system.categoryId
      }
    }));
    return {
      entities: [
        {
          entityType: 'order',
          sourceId: 'order',
          ordinal: 0,
          values: orderValues
        },
        ...systemEntities
      ]
    };
  }
});

const BOM_STATISTICS_ADAPTER = Object.freeze({
  id: 'bom.article_usage',
  systemCode: 'core',
  label: 'Artikelverwendung',
  schemaVersion: 1,
  attributes: BOM_STATISTICS_ATTRIBUTES
});

function attribute(key, label, description, entityType, valueType, unit = '', aggregations = DEFAULT_AGGREGATIONS) {
  return Object.freeze({
    key,
    label,
    description,
    entityType,
    valueType,
    unit,
    activeByDefault: true,
    introducedInVersion: 1,
    aggregations: Object.freeze([...aggregations])
  });
}

function normalizeText(value) {
  return String(value ?? '').trim();
}

function positiveInteger(value, fallback = 1) {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : fallback;
}

function jsonValue(value) {
  if (value === undefined || typeof value === 'function' || typeof value === 'symbol') return undefined;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_error) {
    return undefined;
  }
}

function normalizeAttributeDefinition(raw = {}, adapterVersion = 1) {
  const key = normalizeText(raw.key);
  const entityType = normalizeText(raw.entityType);
  const valueType = STATISTICS_VALUE_TYPES.has(raw.valueType) ? raw.valueType : '';
  if (!key || !entityType || !valueType) return null;
  const aggregations = [...new Set(
    (Array.isArray(raw.aggregations) ? raw.aggregations : DEFAULT_AGGREGATIONS)
      .map(normalizeText)
      .filter(Boolean)
  )];
  return {
    key,
    label: normalizeText(raw.label) || key,
    description: normalizeText(raw.description),
    entityType,
    valueType,
    unit: normalizeText(raw.unit),
    activeByDefault: raw.activeByDefault !== false,
    introducedInVersion: Math.min(
      positiveInteger(raw.introducedInVersion, 1),
      positiveInteger(adapterVersion, 1)
    ),
    aggregations: aggregations.length ? aggregations : [...DEFAULT_AGGREGATIONS]
  };
}

function normalizeSuggestedQueryTemplate(raw = {}) {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const id = normalizeText(source.id);
  const label = normalizeText(source.label);
  const draft = source.draft && typeof source.draft === 'object' && !Array.isArray(source.draft)
    ? source.draft
    : null;
  if (!id || !label || !draft) return null;
  return {
    id,
    label,
    requiresMetric: normalizeText(source.requiresMetric),
    draft
  };
}

function normalizeStatisticsAdapter(raw = {}, fallbackSystem = null) {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const adapterCode = normalizeText(source.adapterCode || source.id || fallbackSystem?.id);
  if (!adapterCode) return null;
  const schemaVersion = positiveInteger(source.schemaVersion, 1);
  const byKey = new Map();
  for (const definition of Array.isArray(source.attributes) ? source.attributes : []) {
    const normalized = normalizeAttributeDefinition(definition, schemaVersion);
    if (normalized && !byKey.has(normalized.key)) byKey.set(normalized.key, normalized);
  }
  if (!byKey.size) return null;
  const suggestedQueryTemplates = Array.isArray(source.suggestedQueryTemplates)
    ? source.suggestedQueryTemplates.map(normalizeSuggestedQueryTemplate).filter(Boolean)
    : [];
  return {
    adapterCode,
    systemCode: normalizeText(source.systemCode || fallbackSystem?.id),
    label: normalizeText(source.label || fallbackSystem?.label || fallbackSystem?.name) || adapterCode,
    schemaVersion,
    attributes: [...byKey.values()],
    suggestedQueryTemplates,
    collect: typeof source.collect === 'function' ? source.collect : null
  };
}

function normalizeValue(value, valueType) {
  if (value === null || value === undefined) return undefined;
  if (valueType === 'number') {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }
  if (valueType === 'boolean') return typeof value === 'boolean' ? value : undefined;
  if (valueType === 'json') return jsonValue(value);
  if (valueType === 'date') {
    const text = normalizeText(value);
    return text && !Number.isNaN(new Date(text).getTime()) ? text : undefined;
  }
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : undefined;
}

function normalizeStatisticsEntities(rawEntities = [], definitions = []) {
  const definitionsByKey = new Map(definitions.map(definition => [definition.key, definition]));
  const seen = new Set();
  const entities = [];
  for (const [index, raw] of (Array.isArray(rawEntities) ? rawEntities : []).entries()) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const entityType = normalizeText(raw.entityType || raw.type);
    const sourceId = normalizeText(raw.sourceId || raw.id);
    if (!entityType || !sourceId) continue;
    const identity = `${entityType}:${sourceId}`;
    if (seen.has(identity)) continue;
    seen.add(identity);
    const rawValues = raw.values && typeof raw.values === 'object' && !Array.isArray(raw.values)
      ? raw.values
      : {};
    const values = {};
    for (const [key, value] of Object.entries(rawValues)) {
      const definition = definitionsByKey.get(key);
      if (!definition || definition.entityType !== entityType) continue;
      const normalized = normalizeValue(value, definition.valueType);
      if (normalized !== undefined) values[key] = normalized;
    }
    entities.push({
      entityType,
      sourceId,
      parentSourceId: normalizeText(raw.parentSourceId || raw.parentId),
      groupSourceId: normalizeText(raw.groupSourceId || raw.groupId),
      ordinal: Number.isInteger(Number(raw.ordinal)) && Number(raw.ordinal) >= 0
        ? Number(raw.ordinal)
        : index,
      values
    });
  }
  return entities;
}

function adapterCatalogEntry(adapter, fallbackSystem = null) {
  const normalized = normalizeStatisticsAdapter(adapter, fallbackSystem);
  if (!normalized) return null;
  return {
    adapterCode: normalized.adapterCode,
    systemCode: normalized.systemCode,
    label: normalized.label,
    schemaVersion: normalized.schemaVersion,
    attributes: normalized.attributes.map(definition => ({ ...definition })),
    suggestedQueryTemplates: normalized.suggestedQueryTemplates.map(template => ({ ...template }))
  };
}

async function collectAdapterSnapshot(adapter, context = {}, fallbackSystem = null) {
  const normalized = normalizeStatisticsAdapter(adapter, fallbackSystem);
  if (!normalized || typeof normalized.collect !== 'function') return null;
  const result = await normalized.collect(context);
  return {
    adapterCode: normalized.adapterCode,
    systemCode: normalized.systemCode,
    label: normalized.label,
    schemaVersion: normalized.schemaVersion,
    attributes: normalized.attributes.map(definition => ({ ...definition })),
    entities: normalizeStatisticsEntities(result?.entities, normalized.attributes)
  };
}

async function safelyLoadCatalogSystems(registry, logger) {
  if (typeof registry?.ensureSystemLoaded !== 'function') return ['registry-loader-unavailable'];
  const ids = (registry.allSystems?.() || [])
    .map(system => normalizeText(system?.id))
    .filter(Boolean);
  const results = await Promise.allSettled(
    ids.map(id => Promise.resolve().then(() => registry.ensureSystemLoaded(id)))
  );
  const loadFailures = [];
  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      loadFailures.push(ids[index]);
      logger?.warn?.(`Statistikadapter fuer System ${ids[index]} konnte nicht geladen werden.`, result.reason);
    }
  });
  return loadFailures;
}

async function collectStatisticsCatalog(options = {}) {
  const registry = options.registry || SystemRegistry;
  const logger = options.logger || console;
  const loadFailures = options.loadSystems === true
    ? await safelyLoadCatalogSystems(registry, logger)
    : [];
  const registeredSystems = registry.allSystems?.() || [];
  const visibleSystems = registeredSystems
    .filter(system => system?.active !== false && system?.hiddenFromNavigation !== true);
  const adapters = [
    adapterCatalogEntry(CORE_STATISTICS_ADAPTER),
    adapterCatalogEntry(BOM_STATISTICS_ADAPTER),
    ...registeredSystems.map(system => (
      adapterCatalogEntry(system?.statisticsAdapter, system)
    ))
  ].filter(Boolean);
  const byCode = new Map();
  adapters.forEach(adapter => {
    if (!byCode.has(adapter.adapterCode)) byCode.set(adapter.adapterCode, adapter);
  });
  return {
    adapters: [...byCode.values()],
    systems: visibleSystems.map(system => ({
      id: normalizeText(system.id),
      code: normalizeText(system.id),
      label: normalizeText(system.label || system.name) || normalizeText(system.id),
      hasStatisticsAdapter: Boolean(adapterCatalogEntry(system?.statisticsAdapter, system))
    })),
    complete: loadFailures.length === 0,
    loadFailures
  };
}

async function collectStatisticsSnapshot(options = {}) {
  const project = options.project && typeof options.project === 'object' ? options.project : {};
  const registry = options.registry || SystemRegistry;
  const logger = options.logger || console;
  if (typeof registry.ensureSystemsForProject === 'function') {
    await registry.ensureSystemsForProject(project, options.ensureSystemOptions || {});
  }
  const presentSystemIds = Array.isArray(options.presentSystemIds)
    ? options.presentSystemIds.map(normalizeText).filter(Boolean)
    : (registry.systemIdsForProject?.(project, options.ensureSystemOptions || {}) || []);
  const presentSystems = presentSystemIds
    .map(systemId => registry.getSystem?.(systemId))
    .filter(Boolean);
  const adapters = [];
  const core = await collectAdapterSnapshot(CORE_STATISTICS_ADAPTER, { project, presentSystems });
  if (core) adapters.push(core);

  for (const system of presentSystems) {
    try {
      const snapshot = await collectAdapterSnapshot(system.statisticsAdapter, {
        project,
        system,
        presentSystems
      }, system);
      if (snapshot) adapters.push(snapshot);
    } catch (error) {
      logger?.warn?.(`Statistikadapter ${system.id} konnte nicht ausgewertet werden.`, error);
      throw new Error(`Statistikadapter ${system.id} konnte nicht ausgewertet werden.`, { cause: error });
    }
  }

  const articleUsageCollector = options.articleUsageCollector || collectBomUsageSnapshot;
  let articleUsage;
  try {
    articleUsage = await articleUsageCollector({
      project,
      ...(options.bomOptions || {})
    });
  } catch (error) {
    logger?.warn?.('BOM-Artikelverwendung konnte nicht fuer den Statistik-Snapshot ermittelt werden.', error);
    throw new Error('BOM-Artikelverwendung konnte nicht fuer den Statistik-Snapshot ermittelt werden.', { cause: error });
  }
  if (!Array.isArray(articleUsage)) throw new Error('BOM-Artikelverwendung hat ein ungueltiges Ergebnis geliefert.');

  return {
    schemaVersion: STATISTICS_SNAPSHOT_SCHEMA_VERSION,
    capturedAt: options.capturedAt || new Date().toISOString(),
    adapters,
    articleUsage
  };
}

export {
  BOM_STATISTICS_ADAPTER,
  CORE_STATISTICS_ADAPTER,
  STATISTICS_SNAPSHOT_SCHEMA_VERSION,
  adapterCatalogEntry,
  collectAdapterSnapshot,
  collectStatisticsCatalog,
  collectStatisticsSnapshot,
  normalizeAttributeDefinition,
  normalizeStatisticsAdapter,
  normalizeStatisticsEntities
};
